import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, HelpCircle, ArrowRight, MapPin, Hammer, ShieldCheck } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { Faq } from '@/lib/seoSchema';
import { BRAND, LEAD_PRICE, money } from '@/data/constants';

/** Visible breadcrumb trail (pairs with BreadcrumbList schema). */
export const Breadcrumbs: React.FC<{ items: { name: string; url?: string }[] }> = ({ items }) => (
  <nav aria-label="Breadcrumb" className="mb-4">
    <ol className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
      {items.map((it, i) => (
        <li key={`${it.name}-${i}`} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="h-3 w-3 text-slate-300" aria-hidden />}
          {it.url ? (
            <Link to={it.url} className="hover:text-orange-600 hover:underline">
              {it.name}
            </Link>
          ) : (
            <span className="font-semibold text-slate-700">{it.name}</span>
          )}
        </li>
      ))}
    </ol>
  </nav>
);

export const PageHero: React.FC<{
  eyebrow?: string;
  title: string;
  intro: string;
  children?: React.ReactNode;
}> = ({ eyebrow, title, intro, children }) => (
  <header className="border-b border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 text-white sm:py-16">
    <div className="mx-auto max-w-6xl px-4">
      {eyebrow && (
        <span className="inline-block rounded-full bg-orange-500/15 px-3 py-1 text-xs font-bold uppercase tracking-wide text-orange-300">
          {eyebrow}
        </span>
      )}
      <h1 className="mt-3 text-3xl font-black leading-tight sm:text-4xl lg:text-5xl">{title}</h1>
      <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-300 sm:text-lg">{intro}</p>
      {children && <div className="mt-6 flex flex-wrap gap-3">{children}</div>}
    </div>
  </header>
);

/**
 * AEO ANSWER BLOCK — a short, direct, extractable answer placed high on the
 * page. This is the block AI Overviews, Perplexity and Copilot quote.
 */
export const AnswerBlock: React.FC<{ question: string; answer: string }> = ({ question, answer }) => (
  <section className="rounded-2xl border-l-4 border-orange-500 bg-orange-50 p-5 sm:p-6">
    <h2 className="flex items-start gap-2 text-base font-black text-slate-900 sm:text-lg">
      <HelpCircle className="mt-0.5 h-5 w-5 shrink-0 text-orange-600" aria-hidden />
      {question}
    </h2>
    <p className="mt-2 text-sm leading-relaxed text-slate-700 sm:text-base">{answer}</p>
  </section>
);

export const Section: React.FC<{ title: string; children: React.ReactNode; id?: string }> = ({ title, children, id }) => (
  <section id={id} className="mt-10">
    <h2 className="text-xl font-black text-slate-900 sm:text-2xl">{title}</h2>
    <div className="mt-4 space-y-4 text-sm leading-relaxed text-slate-700 sm:text-base">{children}</div>
  </section>
);

/** Question + answer list rendered as real HTML (crawlable, no JS required). */
export const FaqSection: React.FC<{ faqs: Faq[]; title?: string }> = ({ faqs, title }) => {
  const { t } = useI18n();
  return (
    <section className="mt-12" id="faq">
      <h2 className="text-xl font-black text-slate-900 sm:text-2xl">{title || t('seo.faqTitle')}</h2>
      <dl className="mt-4 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
        {faqs.map((f) => (
          <div key={f.q} className="p-5">
            <dt className="text-sm font-bold text-slate-900 sm:text-base">{f.q}</dt>
            <dd className="mt-2 text-sm leading-relaxed text-slate-600">{f.a}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

/** Dense internal-link grid — prevents orphan pages. */
export const LinkGrid: React.FC<{
  title: string;
  links: { label: string; to: string }[];
  columns?: number;
  icon?: 'map' | 'trade' | 'none';
}> = ({ title, links, columns = 4, icon = 'none' }) => {
  if (!links.length) return null;
  const Icon = icon === 'map' ? MapPin : icon === 'trade' ? Hammer : null;
  const colClass =
    columns === 2 ? 'sm:grid-cols-2' : columns === 3 ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2 lg:grid-cols-4';
  return (
    <section className="mt-10">
      <h2 className="text-lg font-black text-slate-900 sm:text-xl">{title}</h2>
      <ul className={`mt-4 grid grid-cols-1 gap-x-6 gap-y-2 ${colClass}`}>
        {links.map((l) => (
          <li key={l.to + l.label}>
            <Link
              to={l.to}
              className="group flex items-center gap-1.5 py-1 text-sm text-slate-600 hover:text-orange-600"
            >
              {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400 group-hover:text-orange-500" aria-hidden />}
              <span className="hover:underline">{l.label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};

/** Entity list — machine-readable facts AI systems extract cleanly. */
export const EntityFacts: React.FC<{ rows: { k: string; v: string }[] }> = ({ rows }) => (
  <dl className="mt-6 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
    {rows.map((r) => (
      <div key={r.k} className="bg-white p-4">
        <dt className="text-xs font-bold uppercase tracking-wide text-slate-500">{r.k}</dt>
        <dd className="mt-1 text-sm font-semibold text-slate-900">{r.v}</dd>
      </div>
    ))}
  </dl>
);

export const CtaBand: React.FC<{ variant?: 'sub' | 'post' }> = ({ variant = 'sub' }) => {
  const { t, path } = useI18n();
  return (
    <section className="mt-12 overflow-hidden rounded-2xl bg-slate-900 p-6 text-white sm:p-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-black sm:text-2xl">
            {variant === 'sub' ? t('seo.ctaSubTitle') : t('seo.ctaPostTitle')}
          </h2>
          <p className="mt-1 max-w-xl text-sm text-slate-300">
            {variant === 'sub' ? t('seo.ctaSubBody', { price: money(LEAD_PRICE) }) : t('seo.ctaPostBody')}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-3">
          <Link
            to={path(variant === 'sub' ? '/register' : '/register')}
            className="inline-flex items-center gap-1.5 rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold text-white hover:bg-orange-600"
          >
            {variant === 'sub' ? t('seo.ctaSubBtn') : t('seo.ctaPostBtn')} <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to={path('/jobs')}
            className="inline-flex items-center gap-1.5 rounded-xl border border-white/20 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
          >
            {t('seo.ctaBrowse')}
          </Link>
        </div>
      </div>
    </section>
  );
};

/** Trust strip reused across money pages. */
export const TrustStrip: React.FC = () => {
  const { t } = useI18n();
  const items = [t('seo.trust1'), t('seo.trust2'), t('seo.trust3'), t('seo.trust4')];
  return (
    <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((i) => (
        <li key={i} className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
          {i}
        </li>
      ))}
    </ul>
  );
};

export const PageWrap: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mx-auto max-w-6xl px-4 py-10 sm:py-12">{children}</div>
);

export const brandName = BRAND.fullName;
